import { VenueCreateDialog } from "@/components/VenueCreateDialog";
import { VenueEditDialog } from "@/components/VenueEditDialog";
import {
  useDeleteVenue,
  useUpdateVenue,
  useVenueList,
} from "@/hooks/use-venue-management";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  ArrowUpCircle,
  Ban,
  CheckCircle,
  MoreVertical,
  Plus,
  Store,
} from "lucide-react";
import { useState } from "react";

type VenueItem = {
  id: string;
  name: string;
  strategy: string | null;
  disabled: number;
};

export const Route = createFileRoute("/_app/settings/venues")({
  component: VenuesPage,
});

function VenuesPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useVenueList();
  const updateVenue = useUpdateVenue();
  const deleteVenue = useDeleteVenue();

  const [createOpen, setCreateOpen] = useState(false);
  const [promoteTarget, setPromoteTarget] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<{
    id: string;
    name: string;
    strategy: string | null;
  } | null>(null);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);

  const handleToggleDisabled = (id: string, currentDisabled: number) => {
    updateVenue.mutate({
      id,
      data: { disabled: currentDisabled === 0 ? 1 : 0 },
    });
  };

  const handleDelete = (id: string, name: string) => {
    if (!window.confirm(`「${name}」を削除しますか？`)) return;
    deleteVenue.mutate(id);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* ヘッダー */}
      <header className="flex items-center justify-between border-b px-4 py-3">
        <button
          type="button"
          onClick={() => navigate({ to: "/settings" })}
          className="rounded-full p-1 text-muted-foreground hover:bg-muted"
          aria-label="戻る"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-lg font-bold text-foreground">お店の管理</h1>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="rounded-full p-1 text-muted-foreground hover:bg-muted"
          aria-label="追加"
        >
          <Plus className="h-5 w-5" />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        {isLoading ? (
          <p className="py-8 text-center text-muted-foreground">
            読み込み中...
          </p>
        ) : (
          <>
            {/* 検証済みセクション */}
            <h2 className="text-sm font-semibold text-foreground">検証済み</h2>
            {data?.verified && data.verified.length > 0 ? (
              <ul className="mt-2">
                {data.verified.map((venue: VenueItem) => (
                  <li
                    key={venue.id}
                    className="relative flex items-center gap-3 border-b py-3"
                  >
                    <Store
                      className={`h-4 w-4 shrink-0 text-muted-foreground ${venue.disabled ? "opacity-50" : ""}`}
                    />
                    <div
                      className={`min-w-0 flex-1 ${venue.disabled ? "opacity-50" : ""}`}
                    >
                      <p className="truncate text-sm text-foreground">
                        {venue.name}
                      </p>
                      {venue.strategy && (
                        <p className="truncate text-xs text-muted-foreground">
                          {venue.strategy}
                        </p>
                      )}
                    </div>
                    {venue.disabled ? (
                      <Ban className="h-4 w-4 shrink-0 text-destructive opacity-50" />
                    ) : (
                      <CheckCircle className="h-4 w-4 shrink-0 text-primary" />
                    )}
                    <button
                      type="button"
                      onClick={() =>
                        setMenuOpenId(menuOpenId === venue.id ? null : venue.id)
                      }
                      className="rounded-full p-1 text-muted-foreground hover:bg-muted"
                    >
                      <MoreVertical className="h-4 w-4" />
                    </button>

                    {/* ドロップダウンメニュー */}
                    {menuOpenId === venue.id && (
                      <>
                        <div
                          className="fixed inset-0 z-40"
                          onClick={() => setMenuOpenId(null)}
                          onKeyDown={() => {}}
                          role="presentation"
                        />
                        <div className="absolute right-0 top-full z-50 w-40 rounded-md border bg-background py-1 shadow-lg">
                          <button
                            type="button"
                            onClick={() => {
                              setEditTarget({
                                id: venue.id,
                                name: venue.name,
                                strategy: venue.strategy,
                              });
                              setMenuOpenId(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-muted"
                          >
                            編集
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              handleToggleDisabled(venue.id, venue.disabled);
                              setMenuOpenId(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-muted"
                          >
                            {venue.disabled ? "有効化" : "無効化"}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              handleDelete(venue.id, venue.name);
                              setMenuOpenId(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-destructive hover:bg-muted"
                          >
                            削除
                          </button>
                        </div>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 text-sm text-muted-foreground">
                検証済みのお店はありません
              </p>
            )}

            {/* 未検証セクション */}
            {data?.unverified && data.unverified.length > 0 && (
              <>
                <h2 className="mt-6 text-sm font-semibold text-foreground">
                  未検証（AI 推測）
                </h2>
                <ul className="mt-2">
                  {data.unverified.map((name: string) => (
                    <li
                      key={name}
                      className="flex items-center gap-3 border-b py-3"
                    >
                      <Store className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="flex-1 text-sm text-foreground">
                        {name}
                      </span>
                      <button
                        type="button"
                        onClick={() => setPromoteTarget(name)}
                        className="flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary"
                      >
                        <ArrowUpCircle className="h-3 w-3" />
                        昇格
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </div>

      {/* ダイアログ */}
      <VenueCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
      <VenueCreateDialog
        open={promoteTarget !== null}
        onClose={() => setPromoteTarget(null)}
        initialName={promoteTarget ?? undefined}
        isPromote
      />
      {editTarget && (
        <VenueEditDialog
          open={true}
          onClose={() => setEditTarget(null)}
          venueId={editTarget.id}
          currentName={editTarget.name}
          currentStrategy={editTarget.strategy}
        />
      )}
    </div>
  );
}
